/* eslint-disable react-hooks/exhaustive-deps */
import { Configurator } from '@idearoom/sdk';
import React, { useEffect, useRef, useState } from 'react';
import { Box, Skeleton, Theme, useTheme } from '@mui/material';
import { FloorplanLabelingMethod } from '@idearoom/types';
import { styled } from '@mui/styles';
import { mapClientIdToConfiguratorAndVendor } from '../../utils/clientIdUtils';
import { getConfiguratorUrlWithLocale } from '../../utils/vendorUtils';
import { useAppSelector } from '../../hooks';
import { useClientDataRepo } from '../../hooks/useClientDataRepo';
import { config } from '../../config/config';
import { isNotSiteDetailBranch } from '../../utils/vendorDataUtils';

const INITIAL_WIDTH = 500;
const IMAGE_RATIO = 1.75;

const FloorplanContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  transition: theme.transitions.create(['padding-top', 'width'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflow: 'hidden',
  position: 'relative',
  height: '0px',
}));

export const FloorplanPreview: React.FC<{
  initializing?: boolean;
}> = ({ initializing = false }) => {
  const theme = useTheme();

  const clientId = useAppSelector((state) => state.clientData.clientId);
  const {
    vendorData: { locale, productionURL },
    selectedTableData,
  } = useClientDataRepo({
    useVendorData: true,
    useSelectedTableData: true,
    skipClientDataFetch: isNotSiteDetailBranch,
  });

  const vendorRow = selectedTableData[0];
  const vendorRowExists = !!vendorRow;
  const {
    useFloorPlanGridDimensions,
    floorplanLabelingMethod,
    floorplanShowSideTexts,
    useFloorPlanGridDimensionsLegend,
  } = vendorRow || {};

  const [configuratorSdk, setConfiguratorSdk] = useState<Configurator | null>(null);
  const [floorplan, setFloorplan] = useState<undefined | { src: string; height: number; width: number }>();
  const [transitioning, setTransitioning] = useState(false);
  const isGenerating = useRef(false);
  const nextGenerate = useRef<{ (): Promise<void> } | null>(null);

  useEffect(() => {
    if (!clientId) return undefined;

    let sdk: Configurator;
    const initializeSdk = async () => {
      const { configurator: product, vendor } = mapClientIdToConfiguratorAndVendor(clientId);
      let configuratorUrl = getConfiguratorUrlWithLocale(product, vendor, locale, productionURL);
      if (config.environment.STAGE !== 'localhost') {
        // Force use HTTPS as our SDK doesn't allow non-secure URLs inside secure sites
        configuratorUrl = configuratorUrl.replace('http://', 'https://');
      }
      sdk = new Configurator(configuratorUrl, 'embedded-configurator');

      try {
        // Wait for configurator to connect
        await new Promise<void>((resolve, reject) => {
          const errorListener = (e: any) => {
            reject(e);
            sdk.removeOnErrorListener(errorListener);
          };

          sdk.onConnected = async () => {
            resolve();
            sdk.removeOnErrorListener(errorListener);
          };
          sdk.addOnErrorListener(errorListener);
        });

        setConfiguratorSdk(sdk);
      } catch (e: any) {
        console.error(e);
      }
    };

    initializeSdk();

    return () => {
      sdk?.destroy();
      setConfiguratorSdk(null);
    };
  }, [clientId, locale, productionURL]);

  useEffect(() => {
    if (configuratorSdk && vendorRowExists) {
      const generateFloorplan = async () => {
        isGenerating.current = true;
        try {
          const configuratorFloorplan =
            (await configuratorSdk.generateFloorplan(
              !!useFloorPlanGridDimensions,
              (floorplanLabelingMethod || FloorplanLabelingMethod.LabelsInLegend) as FloorplanLabelingMethod,
              !!useFloorPlanGridDimensionsLegend,
              floorplanShowSideTexts === undefined || floorplanShowSideTexts === null ? true : !!floorplanShowSideTexts,
            )) || {};
          if (configuratorFloorplan) {
            setFloorplan((prev) => {
              if (!prev) {
                setTransitioning(true);
                setTimeout(() => setTransitioning(false), theme.transitions.duration.enteringScreen);
              }
              return configuratorFloorplan;
            });
          }

          const next = nextGenerate.current;
          if (next) {
            nextGenerate.current = null;
            next();
          }
        } finally {
          isGenerating.current = false;
        }
      };

      if (isGenerating.current) {
        nextGenerate.current = generateFloorplan;
      } else {
        generateFloorplan();
      }
    }
    return undefined;
  }, [
    configuratorSdk,
    floorplanLabelingMethod,
    floorplanShowSideTexts,
    useFloorPlanGridDimensions,
    useFloorPlanGridDimensionsLegend,
    !!vendorRowExists,
  ]);

  return (
    <FloorplanContainer
      sx={{
        width: `min(${(floorplan?.width || INITIAL_WIDTH * IMAGE_RATIO) / IMAGE_RATIO}px, 100%)`,
        // Padding % is based on the element's width, so 100% is equivalent to a 1/1 aspect ratio
        // while still respecting a transition
        paddingTop: !floorplan
          ? `min(${INITIAL_WIDTH}px, 100%)`
          : // Calculate the true aspect ratio
            `min(${floorplan.height / IMAGE_RATIO}px, ${((floorplan.height || 1) / (floorplan.width || 1)) * 100}%)`,
      }}
    >
      <div style={{ height: '0px', width: '0px' }} id="embedded-configurator" />
      {!floorplan || initializing || transitioning ? (
        <Skeleton
          sx={{
            WebkitTransform: 'none',
            transform: 'none',
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        />
      ) : (
        <Box
          component="img"
          src={floorplan?.src}
          sx={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
        />
      )}
    </FloorplanContainer>
  );
};
